import { EmailTemplate } from "@/types/breeze/EmailTemplate";
import { BaseService } from "../BaseService";

interface EmailTemplatePreviewResponse {
  subject: string;
  html_content: string;
  sample_data: Record<string, string>;
}

export class EmailTemplateService extends BaseService {
  public async getAllTemplates(): Promise<EmailTemplate[]> {
    return this.getRequest<EmailTemplate[]>("/email-templates");
  }

  public async getTemplate(templateType: string): Promise<EmailTemplate> {
    return this.getRequest<EmailTemplate>(`/email-templates/${templateType}`);
  }

  public async getTemplatePreview(templateType: string): Promise<string> {
    const response = await this.getRequest<EmailTemplatePreviewResponse>(
      `/email-templates/${templateType}/preview`,
    );
    return response.html_content;
  }

  public async previewRaw(data: {
    subject?: string;
    content?: string;
    wrapper_html?: string;
    variables?: Record<string, unknown>;
  }): Promise<string> {
    const response = await this.postRequest<
      typeof data,
      EmailTemplatePreviewResponse
    >(
      "/email-templates/preview-raw",
      data,
      undefined,
      true,
      true,
    );
    return response.html_content;
  }

  public async getShell(): Promise<{ html: string; is_customised: boolean; default_html: string; design_config_json: string | null }> {
    return this.getRequest("/email-templates/shell");
  }

  public async updateShell(html: string | null, designConfigJson: string | null = null): Promise<{ html: string; design_config_json: string | null }> {
    return this.putRequest<
      { html: string | null; design_config_json: string | null },
      { html: string; design_config_json: string | null }
    >(
      "/email-templates/shell",
      { html, design_config_json: designConfigJson },
      undefined,
      true,
      true,
    );
  }

  public async updateTemplate(
    templateType: string,
    updatedTemplate: Partial<EmailTemplate>,
  ): Promise<EmailTemplate> {
    return this.putRequest<Partial<EmailTemplate>, EmailTemplate>(
      `/email-templates/${templateType}`,
      updatedTemplate,
      undefined,
      true,
      true,
    );
  }

  public async createTemplate(newTemplate: {
    template_type: string;
    subject: string;
    content: string;
    html_template?: string;
  }): Promise<EmailTemplate> {
    return this.postRequest<typeof newTemplate, EmailTemplate>(
      "/email-templates",
      newTemplate,
    );
  }

  public async deleteTemplate(templateType: string): Promise<void> {
    await this.deleteRequest(`/email-templates/${templateType}`);
  }
}

export const emailTemplateService = new EmailTemplateService();
